'use client'

import { type ReactNode, type RefObject, useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'
import { getRenderedScale } from '@/lib/utils/css-zoom'

interface OverlayScrollXProps {
  /** The scrolling element, for callers that also need to move it (e.g. keep a tab in view). */
  scrollerRef: RefObject<HTMLDivElement | null>
  className?: string
  children: ReactNode
}

/**
 * A horizontal scroller for tight strips (tab rows): the native scrollbar is hidden so it takes
 * no height, a mouse wheel's vertical scroll moves it sideways, and a thin overlay thumb shows
 * along the bottom edge while hovered — draggable, and drawn over the content, not under it.
 */
export function OverlayScrollX({ scrollerRef, className, children }: OverlayScrollXProps) {
  return (
    <div className={cn('group/scroll-x relative min-w-0', className)}>
      <div ref={scrollerRef} className="scrollbar-none overflow-x-auto">
        {children}
      </div>
      <OverlayThumbX scroller={scrollerRef} wheelToHorizontal />
    </div>
  )
}

interface OverlayThumbXProps {
  /** The horizontally scrolling element: a ref, or the element itself once mounted. */
  scroller: RefObject<HTMLElement | null> | HTMLElement | null
  /**
   * Turn a plain mouse wheel's vertical scroll into horizontal movement — for strips that
   * only scroll sideways. Leave off where the wheel already scrolls vertically (tables).
   */
  wheelToHorizontal?: boolean
}

/**
 * A thin, draggable horizontal thumb drawn over the bottom edge of `scroller`, shown while an
 * ancestor `group/scroll-x` is hovered. Render it as an absolutely positioned sibling of the
 * scroller inside that (positioned) ancestor; the scroller hides its own horizontal bar.
 */
export function OverlayThumbX({
  scroller: scrollerProp,
  wheelToHorizontal = false,
}: OverlayThumbXProps) {
  const thumbRef = useRef<HTMLDivElement>(null)
  const resolveScroller = () =>
    scrollerProp && 'current' in scrollerProp ? scrollerProp.current : scrollerProp

  useEffect(() => {
    const scroller = resolveScroller()
    const thumb = thumbRef.current
    if (!scroller || !thumb) return

    // Written straight to the DOM: scroll fires every frame, and nothing else needs to know.
    const syncThumb = () => {
      const { scrollWidth, clientWidth, scrollLeft } = scroller
      if (scrollWidth <= clientWidth + 1) {
        thumb.style.display = 'none'
        return
      }
      const width = Math.max(24, (clientWidth / scrollWidth) * clientWidth)
      const maxLeft = clientWidth - width
      const left = (scrollLeft / (scrollWidth - clientWidth)) * maxLeft
      thumb.style.display = ''
      thumb.style.width = `${width}px`
      thumb.style.transform = `translateX(${scroller.offsetLeft + left}px)`
    }

    // A plain mouse wheel only scrolls vertically; turn that into horizontal movement here.
    // Trackpad side-swipes (deltaX dominant) are left to the browser. Only swallow the event
    // while the strip can still move that way, so the page scrolls once it hits an end.
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return
      const maxScroll = scroller.scrollWidth - scroller.clientWidth
      if (maxScroll <= 0) return
      const next = Math.min(maxScroll, Math.max(0, scroller.scrollLeft + event.deltaY))
      if (next === scroller.scrollLeft) return
      event.preventDefault()
      scroller.scrollLeft = next
    }

    syncThumb()
    // The content can grow with no resize to observe (e.g. a tab row capped at the scroller's
    // width gaining a tab) — re-measure on hover too, which is the only time the thumb shows.
    const wrapper = scroller.parentElement
    wrapper?.addEventListener('pointerenter', syncThumb)
    scroller.addEventListener('scroll', syncThumb, { passive: true })
    if (wheelToHorizontal) scroller.addEventListener('wheel', onWheel, { passive: false })
    const resizeObserver = new ResizeObserver(syncThumb)
    resizeObserver.observe(scroller)
    if (scroller.firstElementChild) resizeObserver.observe(scroller.firstElementChild)
    return () => {
      wrapper?.removeEventListener('pointerenter', syncThumb)
      scroller.removeEventListener('scroll', syncThumb)
      scroller.removeEventListener('wheel', onWheel)
      resizeObserver.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollerProp, wheelToHorizontal])

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const scroller = resolveScroller()
    if (!scroller) return
    // Keep the press off the widget's drag handle and the tabs' reorder sensor.
    event.stopPropagation()
    event.preventDefault()
    const thumb = event.currentTarget
    thumb.setPointerCapture(event.pointerId)
    const startX = event.clientX
    const startScroll = scroller.scrollLeft
    // Thumb px → content px: the scrollable distance over the thumb's travel. The thumb's
    // own width counts — clamped to its 24px minimum, `scrollWidth / clientWidth` would
    // overshoot the cursor.
    const thumbTravel = scroller.clientWidth - thumb.offsetWidth
    const ratio = thumbTravel > 0 ? (scroller.scrollWidth - scroller.clientWidth) / thumbTravel : 0
    // Pointer deltas are in viewport px; the thumb and scrollLeft are in local px (CSS zoom).
    const scale = getRenderedScale(scroller)
    const onMove = (move: PointerEvent) => {
      scroller.scrollLeft = startScroll + ((move.clientX - startX) / scale) * ratio
    }
    const onUp = () => {
      thumb.removeEventListener('pointermove', onMove)
      thumb.removeEventListener('pointerup', onUp)
      thumb.removeEventListener('pointercancel', onUp)
    }
    thumb.addEventListener('pointermove', onMove)
    thumb.addEventListener('pointerup', onUp)
    thumb.addEventListener('pointercancel', onUp)
  }

  return (
    <div
      ref={thumbRef}
      aria-hidden
      // SCROLL_X_THUMB_ATTR: dragging it counts as the user scrolling sideways.
      data-scroll-x-thumb
      onPointerDown={handleThumbPointerDown}
      className="no-drag bg-foreground/25 hover:bg-foreground/40 absolute bottom-0 left-0 z-20 h-1 cursor-pointer rounded-full opacity-0 transition-opacity duration-150 group-hover/scroll-x:opacity-100"
      style={{ display: 'none' }}
    />
  )
}
